import { useEffect, useMemo, useState, useCallback } from 'react'
import { adminAPI, tenantsAPI } from '../../lib/api'
import type { AdminTenantListItem, AdminUser, User } from '../../types'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { useToast } from '../../components/ui/useToast'
import { Select } from '../../components/ui/Select'
import { Modal } from '../../components/ui/Modal'
import { getErrorMessage } from '../../lib/utils'
import {
  Building2, Search, RefreshCw, Plus, Users, Shield,
  Settings, CheckCircle, XCircle, Trash2, AlertCircle,
  Calendar, Mail, Lock, UserPlus, Bell, Activity
} from 'lucide-react'

export function AdminTenantsPage() {
  const [user, setUser] = useState<User | null>(null)
  // Early guard: render-only check without conditional hooks
  const isSuperAdmin = user?.role === 'SuperAdmin'
  const [tenants, setTenants] = useState<AdminTenantListItem[]>([])
  const [error, setError] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const [page, setPage] = useState(1)
  const perPage = 10
  const { notify } = useToast()
  const [liveCounts, setLiveCounts] = useState<Record<number, number>>({})
  const [loading, setLoading] = useState(false)
  // Create Company form
  const [showCreate, setShowCreate] = useState(false)
  const [tenantName, setTenantName] = useState('')
  const [subdomain, setSubdomain] = useState('')
  const [adminName, setAdminName] = useState('')
  const [adminEmail, setAdminEmail] = useState('')
  const [adminPassword, setAdminPassword] = useState('')
  const [plan, setPlan] = useState('free')

  // Manage panel
  const [managingId, setManagingId] = useState<number | null>(null)
  const [admins, setAdmins] = useState<AdminUser[]>([])
  const [adminsPage, setAdminsPage] = useState(1)
  const [adminsTotal, setAdminsTotal] = useState(0)
  const adminsPerPage = 10
  const [users, setUsers] = useState<AdminUser[]>([])
  const [usersPage, setUsersPage] = useState(1)
  const [usersTotal, setUsersTotal] = useState(0)
  const usersPerPage = 10
  const [newAdminName, setNewAdminName] = useState('')
  const [newAdminEmail, setNewAdminEmail] = useState('')
  const [newAdminPassword, setNewAdminPassword] = useState('')
  const [newUserName, setNewUserName] = useState('')
  const [newUserEmail, setNewUserEmail] = useState('')
  const [newUserPassword, setNewUserPassword] = useState('')
  const [newUserRole, setNewUserRole] = useState('Receptionist')
  const [editName, setEditName] = useState('')
  const [editPlan, setEditPlan] = useState('free')
  const [editActive, setEditActive] = useState(true)
  // Tenant notification
  const [notifyTitle, setNotifyTitle] = useState('')
  const [notifyBody, setNotifyBody] = useState('')

  useEffect(() => {
    let mounted = true
    ;(async () => {
      try {
        // Fetch admin session
        const sessionRes = await fetch('/api/admin/session', { credentials: 'include' })
        if (sessionRes.ok) {
          const sessionData = await sessionRes.json()
          if (mounted && sessionData.success && sessionData.data?.user) {
            setUser(sessionData.data.user)
          }
        }

        // Fetch tenants
        const res = await adminAPI.tenants()
        if (mounted && res.success) setTenants(Array.isArray(res.data) ? (res.data as AdminTenantListItem[]) : [])
        else if (mounted) setError(res.message || 'Failed to load tenants')
      } catch (e: unknown) { if (mounted) setError(getErrorMessage(e, 'Failed to load')) }
    })()
    return () => { mounted = false }
  }, [])

  // Load live bookings counts
  const loadLiveCounts = useCallback(async () => {
    setLoading(true)
    const res = await adminAPI.tenantsLiveBookings()
    if (res.success) {
      const map: Record<number, number> = {}
      const rows = Array.isArray(res.data) ? res.data as Array<{ tenant_id: number; cnt: number }> : []
      for (const row of rows) map[row.tenant_id] = row.cnt
      setLiveCounts(map)
      notify({ kind: 'success', message: 'Live bookings refreshed' })
    }
    setLoading(false)
  }, [notify])

  useEffect(() => { loadLiveCounts() }, [loadLiveCounts])

  useEffect(() => {
    if (error) notify({ kind: 'error', message: error })
  }, [error, notify])

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim()
    if (!q) return tenants
    return tenants.filter(t => `${t.name} ${t.subdomain} ${t.plan}`.toLowerCase().includes(q))
  }, [tenants, query])

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage))
  const pageItems = filtered.slice((page - 1) * perPage, page * perPage)

  const refreshTenants = async () => {
    const res = await adminAPI.tenants()
  if (res.success) setTenants(Array.isArray(res.data) ? (res.data as AdminTenantListItem[]) : [])
  }

  const createCompany = async () => {
    if (!tenantName.trim() || !adminName.trim() || !adminEmail.trim() || !adminPassword) {
      notify({ kind: 'error', message: 'All required fields must be filled' }); return
    }
    const res = await adminAPI.tenantsCreate({ tenant_name: tenantName.trim(), subdomain: subdomain.trim() || undefined, admin_name: adminName.trim(), admin_email: adminEmail.trim(), admin_password: adminPassword, plan })
    if (res.success) { notify({ kind: 'success', message: 'Company created successfully' }); setShowCreate(false); setTenantName(''); setSubdomain(''); setAdminName(''); setAdminEmail(''); setAdminPassword(''); await refreshTenants() }
    else notify({ kind: 'error', message: res.message || 'Failed to create company' })
  }

  const openManage = async (t: AdminTenantListItem) => {
    setManagingId(t.id)
    setEditName(t.name)
    setEditPlan(t.plan)
    setEditActive(!!t.is_active)
  const res = await adminAPI.tenantAdminsList(t.id, { page: 1, per_page: adminsPerPage })
    if (res.success) { const items = Array.isArray(res.data) ? (res.data as AdminUser[]) : []; setAdmins(items); setAdminsTotal((res.meta?.total_count as number) || items.length); setAdminsPage(1) }
    const ur = await adminAPI.tenantUsersList(t.id, { page: 1, per_page: usersPerPage })
    if (ur.success) { const items = Array.isArray(ur.data) ? (ur.data as AdminUser[]) : []; setUsers(items); setUsersTotal((ur.meta?.total_count as number) || items.length); setUsersPage(1) }
  }

  const saveCompany = async () => {
    if (!managingId) return
    const res = await tenantsAPI.update(managingId, { name: editName, plan: editPlan, is_active: editActive })
    if (res.success) { notify({ kind: 'success', message: 'Company updated' }); await refreshTenants() }
    else notify({ kind: 'error', message: res.message || 'Failed to update' })
  }

  const toggleTenantActive = async (t: AdminTenantListItem) => {
    const action = t.is_active ? adminAPI.tenantSuspend : adminAPI.tenantEnable
    const res = await action(t.id)
    if (res.success) { notify({ kind: 'success', message: t.is_active ? 'Tenant suspended' : 'Tenant enabled' }); await refreshTenants() }
    else notify({ kind: 'error', message: res.message || 'Failed to update' })
  }

  const deleteTenant = async (t: AdminTenantListItem) => {
    if (!confirm(`⚠️ Delete ${t.name}? This action cannot be undone and will remove all associated data.`)) return
    const res = await adminAPI.tenantDelete(t.id)
    if (res.success) { notify({ kind: 'success', message: 'Tenant deleted successfully' }); await refreshTenants() }
    else notify({ kind: 'error', message: res.message || 'Failed to delete' })
  }

  const addCompanyAdmin = async () => {
    if (!managingId) return
    const res = await adminAPI.tenantAdminsCreate(managingId, { name: newAdminName.trim(), email: newAdminEmail.trim(), password: newAdminPassword })
    if (res.success) {
      notify({ kind: 'success', message: 'Admin added successfully' }); setNewAdminName(''); setNewAdminEmail(''); setNewAdminPassword('');
  const list = await adminAPI.tenantAdminsList(managingId, { page: 1, per_page: adminsPerPage })
      if (list.success) { const items = Array.isArray(list.data) ? (list.data as AdminUser[]) : []; setAdmins(items); setAdminsTotal((list.meta?.total_count as number) || items.length); setAdminsPage(1) }
    }
    else notify({ kind: 'error', message: res.message || 'Failed to add admin' })
  }

  const removeCompanyAdmin = async (uid: number) => {
    if (!managingId) return
    const res = await adminAPI.tenantAdminDelete(managingId, uid)
    if (res.success) {
      notify({ kind: 'success', message: 'Admin removed' });
  const list = await adminAPI.tenantAdminsList(managingId, { page: adminsPage, per_page: adminsPerPage })
      if (list.success) { const items = Array.isArray(list.data) ? (list.data as AdminUser[]) : []; setAdmins(items); setAdminsTotal((list.meta?.total_count as number) || items.length) }
    }
    else notify({ kind: 'error', message: res.message || 'Failed to remove admin' })
  }

  const changeAdminRole = async (uid: number, role: string) => {
    if (!managingId) return
    const res = await adminAPI.tenantAdminChangeRole(managingId, uid, role)
    if (res.success) {
      notify({ kind: 'success', message: 'Role updated' });
  const list = await adminAPI.tenantAdminsList(managingId, { page: adminsPage, per_page: adminsPerPage })
      if (list.success) { const items = Array.isArray(list.data) ? (list.data as AdminUser[]) : []; setAdmins(items); setAdminsTotal((list.meta?.total_count as number) || items.length) }
    }
    else notify({ kind: 'error', message: res.message || 'Failed to update role' })
  }

  const addUser = async () => {
    if (!managingId) return
    const res = await adminAPI.tenantUsersCreate(managingId, { name: newUserName.trim(), email: newUserEmail.trim(), password: newUserPassword, role: newUserRole })
    if (res.success) {
      notify({ kind: 'success', message: 'User added successfully' }); setNewUserName(''); setNewUserEmail(''); setNewUserPassword(''); setNewUserRole('Receptionist')
      const ur = await adminAPI.tenantUsersList(managingId, { page: 1, per_page: usersPerPage })
      if (ur.success) { const items = Array.isArray(ur.data) ? (ur.data as AdminUser[]) : []; setUsers(items); setUsersTotal((ur.meta?.total_count as number) || items.length); setUsersPage(1) }
    }
    else notify({ kind: 'error', message: res.message || 'Failed to add user' })
  }

  const sendTenantNotification = async () => {
    if (!managingId) return
    if (!notifyTitle.trim() || !notifyBody.trim()) { notify({ kind: 'error', message: 'Title and body are required' }); return }
    const res = await adminAPI.messagesCreate({ title: notifyTitle.trim(), body: notifyBody.trim(), tenant_id: managingId })
    if (res.success) { notify({ kind: 'success', message: 'Notification sent' }); setNotifyTitle(''); setNotifyBody('') }
    else notify({ kind: 'error', message: res.message || 'Failed to send' })
  }

  if (!isSuperAdmin) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center p-8">
          <Shield className="h-16 w-16 text-red-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">Access Denied</h2>
          <p className="text-gray-600 dark:text-gray-400">You don't have permission to access this page.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
            All Tenants
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2 flex items-center gap-2">
            <Building2 className="h-4 w-4" />
            Manage all tenant organizations and their settings
          </p>
        </div>
        <Button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600"
        >
          <Plus className="h-4 w-4" />
          Add Company
        </Button>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="flex items-start gap-3 p-4 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-800 rounded-xl">
          <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5" />
          <div className="flex-1">
            <h3 className="font-semibold text-red-900 dark:text-red-100">Error</h3>
            <p className="text-sm text-red-700 dark:text-red-300 mt-1">{error}</p>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid sm:grid-cols-3 gap-4">
        <div className="card bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-500/5 dark:to-indigo-500/5 border-blue-200 dark:border-blue-800">
          <div className="card-body">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-blue-600 dark:text-blue-400">Total Tenants</p>
                <p className="text-3xl font-bold mt-2 text-blue-900 dark:text-blue-100">{tenants.length}</p>
              </div>
              <div className="p-3 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-500">
                <Building2 className="h-6 w-6 text-white" />
              </div>
            </div>
          </div>
        </div>
        <div className="card bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-500/5 dark:to-emerald-500/5 border-green-200 dark:border-green-800">
          <div className="card-body">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-green-600 dark:text-green-400">Active Tenants</p>
                <p className="text-3xl font-bold mt-2 text-green-900 dark:text-green-100">
                  {tenants.filter(t => t.is_active).length}
                </p>
              </div>
              <div className="p-3 rounded-xl bg-gradient-to-br from-green-500 to-emerald-500">
                <CheckCircle className="h-6 w-6 text-white" />
              </div>
            </div>
          </div>
        </div>
        <div className="card bg-gradient-to-br from-orange-50 to-red-50 dark:from-orange-500/5 dark:to-red-500/5 border-orange-200 dark:border-orange-800">
          <div className="card-body">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-orange-600 dark:text-orange-400">Live Bookings</p>
                <p className="text-3xl font-bold mt-2 text-orange-900 dark:text-orange-100">
                  {Object.values(liveCounts).reduce((a, b) => a + b, 0)}
                </p>
              </div>
              <div className="p-3 rounded-xl bg-gradient-to-br from-orange-500 to-red-500">
                <Activity className="h-6 w-6 text-white" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tenants Table */}
      <div className="card">
        <div className="card-header flex items-center justify-between">
          <h2 className="font-semibold text-lg flex items-center gap-2">
            <Building2 className="h-5 w-5 text-orange-600" />
            Tenant Organizations
          </h2>
          <Button
            onClick={loadLiveCounts}
            variant="outline"
            disabled={loading}
            className="flex items-center gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            {loading ? 'Refreshing...' : 'Refresh Live Bookings'}
          </Button>
        </div>
        <div className="card-body">
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input
                placeholder="Search tenants by name, subdomain, or plan..."
                value={query}
                onChange={(e) => { setPage(1); setQuery(e.target.value) }}
                className="pl-10"
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800/50">
                <tr className="text-left">
                  <th className="py-3 px-4 font-semibold">Name</th>
                  <th className="py-3 px-4 font-semibold">Subdomain</th>
                  <th className="py-3 px-4 font-semibold">Plan</th>
                  <th className="py-3 px-4 font-semibold">Status</th>
                  <th className="py-3 px-4 font-semibold">Created</th>
                  <th className="py-3 px-4 font-semibold text-center">Live Bookings</th>
                  <th className="py-3 px-4 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
                {pageItems.map(t => (
                  <tr key={t.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                    <td className="py-3 px-4">
                      <div className="font-medium text-gray-900 dark:text-gray-100">{t.name}</div>
                    </td>
                    <td className="py-3 px-4 text-gray-600 dark:text-gray-400">{t.subdomain}</td>
                    <td className="py-3 px-4">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                        t.plan === 'enterprise' ? 'bg-purple-100 dark:bg-purple-500/10 text-purple-700 dark:text-purple-300' :
                        t.plan === 'pro' ? 'bg-blue-100 dark:bg-blue-500/10 text-blue-700 dark:text-blue-300' :
                        'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                      }`}>
                        {t.plan}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      {t.is_active ? (
                        <span className="inline-flex items-center gap-1 text-green-600 dark:text-green-400">
                          <CheckCircle className="h-4 w-4" />
                          <span className="text-sm font-medium">Active</span>
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-red-600 dark:text-red-400">
                          <XCircle className="h-4 w-4" />
                          <span className="text-sm font-medium">Suspended</span>
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-gray-600 dark:text-gray-400 text-xs">
                      {new Date(t.created_at).toLocaleString()}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className="inline-flex items-center justify-center h-8 w-8 rounded-full bg-orange-100 dark:bg-orange-500/10 text-orange-700 dark:text-orange-300 font-semibold">
                        {liveCounts[t.id] ?? 0}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex gap-2">
                        <Button
                          onClick={() => openManage(t)}
                          variant="outline"
                          className="text-xs flex items-center gap-1"
                        >
                          <Settings className="h-3 w-3" />
                          Manage
                        </Button>
                        <Button
                          onClick={() => toggleTenantActive(t)}
                          variant="outline"
                          className={`text-xs ${t.is_active ? 'text-orange-600 border-orange-600 hover:bg-orange-50 dark:hover:bg-orange-500/10' : 'text-green-600 border-green-600 hover:bg-green-50 dark:hover:bg-green-500/10'}`}
                        >
                          {t.is_active ? 'Suspend' : 'Enable'}
                        </Button>
                        <Button
                          onClick={() => deleteTenant(t)}
                          variant="outline"
                          className="text-xs text-red-600 border-red-600 hover:bg-red-50 dark:hover:bg-red-500/10"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-4 flex items-center justify-between border-t border-gray-200 dark:border-gray-800 pt-4">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Page <span className="font-medium">{page}</span> of <span className="font-medium">{totalPages}</span> · <span className="font-medium">{filtered.length}</span> total tenants
            </div>
            <div className="flex gap-2">
              <Button
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                variant="outline"
              >
                Previous
              </Button>
              <Button
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                variant="outline"
              >
                Next
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Create Company Modal */}
      {showCreate && (
        <Modal title="Add New Company" onClose={() => setShowCreate(false)}>
          <div className="space-y-6">
            <div className="p-4 bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-800 rounded-lg">
              <div className="flex items-start gap-3">
                <Building2 className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5" />
                <div className="flex-1 text-sm">
                  <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-1">Create New Tenant Organization</h3>
                  <p className="text-blue-800 dark:text-blue-300">
                    This will create a new tenant with an admin account. The admin will be able to manage their organization independently.
                  </p>
                </div>
              </div>
            </div>

            <div className="grid gap-4">
              <div>
                <label className="form-label flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-gray-500" />
                  Company Name *
                </label>
                <Input value={tenantName} onChange={(e) => setTenantName(e.target.value)} placeholder="Acme Studios Ltd" />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Subdomain</label>
                  <Input value={subdomain} onChange={(e) => setSubdomain(e.target.value)} placeholder="acme (optional)" />
                  <p className="text-xs text-gray-500 mt-1">Leave blank for auto-generation</p>
                </div>
                <div>
                  <label className="form-label">Plan</label>
                  <Select value={plan} onChange={(e) => setPlan(e.target.value)}>
                    <option value="free">Free</option>
                    <option value="pro">Professional</option>
                    <option value="enterprise">Enterprise</option>
                  </Select>
                </div>
              </div>

              <div className="border-t border-gray-200 dark:border-gray-800 pt-4 mt-2">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <Shield className="h-4 w-4 text-orange-600" />
                  Company Administrator
                </h3>
                <div className="grid gap-4">
                  <div>
                    <label className="form-label">Admin Name *</label>
                    <Input value={adminName} onChange={(e) => setAdminName(e.target.value)} placeholder="John Doe" />
                  </div>
                  <div>
                    <label className="form-label flex items-center gap-2">
                      <Mail className="h-4 w-4 text-gray-500" />
                      Admin Email *
                    </label>
                    <Input value={adminEmail} onChange={(e) => setAdminEmail(e.target.value)} type="email" placeholder="john@acme.com" />
                  </div>
                  <div>
                    <label className="form-label flex items-center gap-2">
                      <Lock className="h-4 w-4 text-gray-500" />
                      Admin Password *
                    </label>
                    <Input type="password" value={adminPassword} onChange={(e) => setAdminPassword(e.target.value)} placeholder="••••••••" />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-800">
              <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button onClick={createCompany} className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600">
                <Plus className="h-4 w-4 mr-2" />
                Create Company
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Manage Company Modal */}
      {managingId && (
        <Modal title={`Manage Company · ID ${managingId}`} onClose={() => setManagingId(null)}>
          <div className="space-y-6">
            {/* Company Settings */}
            <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <Settings className="h-4 w-4 text-orange-600" />
                Company Settings
              </h3>
              <div className="grid sm:grid-cols-3 gap-3">
                <div className="sm:col-span-2">
                  <label className="form-label">Company Name</label>
                  <Input value={editName} onChange={(e) => setEditName(e.target.value)} />
                </div>
                <div>
                  <label className="form-label">Plan</label>
                  <Select value={editPlan} onChange={(e) => setEditPlan(e.target.value)}>
                    <option value="free">Free</option>
                    <option value="pro">Professional</option>
                    <option value="enterprise">Enterprise</option>
                  </Select>
                </div>
                <div>
                  <label className="form-label">Status</label>
                  <Select value={editActive ? '1' : '0'} onChange={(e) => setEditActive(e.target.value === '1')}>
                    <option value="1">Active</option>
                    <option value="0">Suspended</option>
                  </Select>
                </div>
                <div className="sm:col-span-3">
                  <Button onClick={saveCompany} className="w-full">Save Settings</Button>
                </div>
              </div>
            </div>

            {/* Company Admins */}
            <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <Shield className="h-4 w-4 text-orange-600" />
                Company Administrators (Max 2)
              </h3>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-white dark:bg-gray-900">
                    <tr className="text-left">
                      <th className="py-2 px-3 font-semibold">Name</th>
                      <th className="py-2 px-3 font-semibold">Email</th>
                      <th className="py-2 px-3 font-semibold">Role</th>
                      <th className="py-2 px-3 font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
                    {admins.map(a => (
                      <tr key={a.id} className="hover:bg-white dark:hover:bg-gray-900 transition-colors">
                        <td className="py-2 px-3">{a.name}</td>
                        <td className="py-2 px-3 text-gray-600 dark:text-gray-400">{a.email}</td>
                        <td className="py-2 px-3">
                          <Select value={a.role} onChange={(e) => changeAdminRole(a.id, e.target.value)} className="text-xs">
                            <option value="Admin">Admin</option>
                            <option value="Studio Manager">Studio Manager</option>
                            <option value="Receptionist">Receptionist</option>
                            <option value="Staff/Instructor">Staff/Instructor</option>
                          </Select>
                        </td>
                        <td className="py-2 px-3">
                          <Button
                            onClick={() => removeCompanyAdmin(a.id)}
                            variant="outline"
                            className="text-xs text-red-600 border-red-600 hover:bg-red-50 dark:hover:bg-red-500/10"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-200 dark:border-gray-800">
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {adminsTotal} admin{adminsTotal !== 1 ? 's' : ''}
                </div>
              </div>

              <div className="grid sm:grid-cols-3 gap-3 mt-4 pt-4 border-t border-gray-200 dark:border-gray-800">
                <div>
                  <label className="form-label text-xs">Admin Name</label>
                  <Input value={newAdminName} onChange={(e) => setNewAdminName(e.target.value)} className="text-sm" placeholder="Name" />
                </div>
                <div>
                  <label className="form-label text-xs">Admin Email</label>
                  <Input value={newAdminEmail} onChange={(e) => setNewAdminEmail(e.target.value)} className="text-sm" placeholder="Email" />
                </div>
                <div>
                  <label className="form-label text-xs">Password</label>
                  <Input type="password" value={newAdminPassword} onChange={(e) => setNewAdminPassword(e.target.value)} className="text-sm" placeholder="Password" />
                </div>
                <div className="sm:col-span-3">
                  <Button onClick={addCompanyAdmin} disabled={adminsTotal >= 2} className="w-full">
                    <UserPlus className="h-4 w-4 mr-2" />
                    Add Administrator
                  </Button>
                </div>
              </div>
            </div>

            {/* Add User */}
            <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <Users className="h-4 w-4 text-orange-600" />
                Add User
              </h3>
              <div className="grid sm:grid-cols-4 gap-3">
                <div>
                  <label className="form-label text-xs">Name</label>
                  <Input value={newUserName} onChange={(e) => setNewUserName(e.target.value)} className="text-sm" />
                </div>
                <div>
                  <label className="form-label text-xs">Email</label>
                  <Input value={newUserEmail} onChange={(e) => setNewUserEmail(e.target.value)} className="text-sm" />
                </div>
                <div>
                  <label className="form-label text-xs">Password</label>
                  <Input type="password" value={newUserPassword} onChange={(e) => setNewUserPassword(e.target.value)} className="text-sm" />
                </div>
                <div>
                  <label className="form-label text-xs">Role</label>
                  <Select value={newUserRole} onChange={(e) => setNewUserRole(e.target.value)} className="text-sm">
                    <option value="Receptionist">Receptionist</option>
                    <option value="Studio Manager">Studio Manager</option>
                    <option value="Staff/Instructor">Staff/Instructor</option>
                    <option value="Admin">Admin</option>
                  </Select>
                </div>
                <div className="sm:col-span-4">
                  <Button onClick={addUser} className="w-full">
                    <UserPlus className="h-4 w-4 mr-2" />
                    Add User
                  </Button>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-800">
                <h4 className="font-medium mb-2 text-sm">Company Users</h4>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-white dark:bg-gray-900">
                      <tr className="text-left">
                        <th className="py-2 px-3 font-semibold text-xs">Name</th>
                        <th className="py-2 px-3 font-semibold text-xs">Email</th>
                        <th className="py-2 px-3 font-semibold text-xs">Role</th>
                        <th className="py-2 px-3 font-semibold text-xs">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
                      {users.map(u => (
                        <tr key={u.id} className="hover:bg-white dark:hover:bg-gray-900 transition-colors">
                          <td className="py-2 px-3">{u.name}</td>
                          <td className="py-2 px-3 text-gray-600 dark:text-gray-400">{u.email}</td>
                          <td className="py-2 px-3 text-xs">{u.role}</td>
                          <td className="py-2 px-3">
                            {u.is_active ? (
                              <span className="inline-flex items-center gap-1 text-green-600 dark:text-green-400 text-xs">
                                <CheckCircle className="h-3 w-3" />
                                Active
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-red-600 dark:text-red-400 text-xs">
                                <XCircle className="h-3 w-3" />
                                Inactive
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex items-center justify-between mt-3">
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    {usersTotal} user{usersTotal !== 1 ? 's' : ''}
                  </div>
                </div>
              </div>
            </div>

            {/* Send Notification */}
            <div className="p-4 bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-800 rounded-xl">
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <Bell className="h-4 w-4 text-blue-600" />
                Send Notification to Tenant
              </h3>
              <div className="grid gap-3">
                <div>
                  <label className="form-label text-xs">Title</label>
                  <Input value={notifyTitle} onChange={(e) => setNotifyTitle(e.target.value)} placeholder="Notification title" />
                </div>
                <div>
                  <label className="form-label text-xs">Message</label>
                  <textarea
                    className="h-28 w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3 text-sm"
                    value={notifyBody}
                    onChange={(e) => setNotifyBody(e.target.value)}
                    placeholder="Your message here..."
                  />
                </div>
                <div>
                  <Button onClick={sendTenantNotification} className="w-full">
                    <Mail className="h-4 w-4 mr-2" />
                    Send Notification
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}
